import React, { useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Shuffle, Check as CheckIcon, X } from "lucide-react";

/**
 * Renders a "3 variations" button. On click, opens a popover with 3 alternate strings/arrays,
 * user clicks one to swap in. onPick(newValue) is called by parent to update the listing.
 */
export default function VariationsMenu({ field, listing, platform, onPick, testId }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [alts, setAlts] = useState([]);

  const fetchAlts = async () => {
    setLoading(true);
    try {
      const r = await http.post("/variations", { field, listing, platform, tone: "professional" });
      setAlts(r.data.variations || []);
      setOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not fetch variations");
    } finally { setLoading(false); }
  };

  const isList = field === "tags" || field === "bullets" || field === "hashtags";

  return (
    <>
      <button
        data-testid={testId || `variations-${field}-${platform}`}
        onClick={fetchAlts}
        disabled={loading}
        className="inline-flex items-center gap-1 text-xs font-semibold text-zinc-600 hover:text-zinc-900"
        title="Regenerate 3 alternates for this field (1 credit)"
      >
        <Shuffle size={12} /> {loading ? "…" : "3 alt"}
      </button>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-xl max-w-lg w-full p-5 shadow-2xl" onClick={e=>e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold">Pick a variation</div>
                <div className="text-lg font-extrabold h-tight">{field}</div>
              </div>
              <button className="p-1 rounded-full hover:bg-zinc-100" onClick={()=>setOpen(false)}><X size={16} /></button>
            </div>
            <div className="space-y-2 max-h-[60vh] overflow-auto">
              {alts.map((a, i) => (
                <button
                  key={i}
                  data-testid={`variation-pick-${field}-${i}`}
                  onClick={() => { onPick(a); setOpen(false); toast.success("Field updated"); }}
                  className="w-full text-left border border-zinc-200 rounded-lg p-3 hover:border-lime hover:bg-lime/5 transition-colors"
                >
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1 flex items-center gap-1"><CheckIcon size={11} /> Option {i+1}</div>
                  {isList ? (
                    <div className="flex flex-wrap gap-1.5">
                      {(Array.isArray(a) ? a : []).map((t, j) => (
                        <span key={j} className="text-xs bg-zinc-100 border border-zinc-200 rounded-full px-2 py-0.5">{t}</span>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-zinc-800">{a}</div>
                  )}
                </button>
              ))}
              {alts.length === 0 && <div className="text-sm text-zinc-500">No variations returned.</div>}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
